import React, { useState, useEffect } from 'react';
import Icon from './AppIcon';
import Button from './ui/Button';

/**
 * Reusable Shipment Tracking Modal
 * 
 * Used across user-account-dashboard and seller-account-dashboard.
 * Displays shop fulfillment master stages, live carrier status banner,
 * auto-expanded carrier detail scans in ascending order,
 * and a direct link to review tracking on the carrier's official website.
 */
const ShipmentTrackingModal = ({
  isOpen,
  onClose,
  carrier = 'Carrier',
  trackingNumber = '',
  serviceType = 'Standard',
  parentItem = null,
  onDelivered = null,
}) => {
  const [carrierScans, setCarrierScans] = useState([]);
  const [loadingCarrierScans, setLoadingCarrierScans] = useState(false);
  const [scansExpanded, setScansExpanded] = useState(true);
  const [isDelivered, setIsDelivered] = useState(false);
  const [deliveryInfo, setDeliveryInfo] = useState({
    status: 'In Transit',
    estimatedDelivery: 'Syncing carrier updates...',
  });

  const hasTracking = !!trackingNumber && trackingNumber !== 'Awaiting Label Creation';
  const isCNC = !!parentItem?.componentId || !!parentItem?.component_id;
  const mfgStatus = parentItem?.manufacturingStatus || parentItem?.manufacturing_status || 'Completed';

  // Construct official carrier tracking website lookup URL
  const getCarrierWebUrl = (cName, trkNum) => {
    if (!trkNum) return null;
    const cUpper = (cName || '').toUpperCase();
    if (cUpper.includes('FEDEX')) {
      return `https://www.fedex.com/fedextrack/?trknbr=${encodeURIComponent(trkNum)}`;
    } else if (cUpper.includes('UPS')) {
      return `https://www.ups.com/track?loc=en_US&tracknum=${encodeURIComponent(trkNum)}`;
    } else if (cUpper.includes('USPS')) {
      return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${encodeURIComponent(trkNum)}`;
    } else if (cUpper.includes('DHL')) {
      return `https://www.dhl.com/en/express/tracking.html?AWB=${encodeURIComponent(trkNum)}`;
    } else if (cUpper.includes('AMAZON')) {
      return `https://track.amazon.com/tracking/${encodeURIComponent(trkNum)}`;
    }
    return null;
  };

  const carrierWebUrl = getCarrierWebUrl(carrier, trackingNumber);

  // Fulfillment Master Stages (Shop Fulfillment)
  const masterSteps = [
    {
      id: 'received',
      status: 'Order Received',
      location: 'Seller Warehouse',
      description: 'Order successfully received. Assigned to manufacturing queue.',
      time: 'Checkout',
      state: 'completed'
    },
    {
      id: 'manufacturing',
      status: isCNC ? 'In CNC Machine' : 'In 3D Print Now',
      location: 'Seller Workshop',
      description: isCNC 
        ? 'Custom toolpath files active. Precision CNC cutting verified.' 
        : 'High-precision 3D print beds completed. Geometry layers cured.',
      time: 'Production',
      state: 'completed'
    },
    {
      id: 'processing',
      status: 'Post Processing & Quality',
      location: 'Seller Workshop',
      description: 'Support material removal, UV curing, detailing, and quality audits.',
      time: 'Quality Control',
      state: 'completed'
    },
    {
      id: 'ready',
      status: 'Ready for Shipment',
      location: 'Seller Warehouse',
      description: 'Quality controls verified. Custom part securely packaged and carrier label created.',
      time: 'Packaged',
      state: 'completed'
    },
    {
      id: 'shipped',
      status: isDelivered ? 'Delivered' : 'Shipped - In Transit',
      location: isDelivered ? (carrierScans[carrierScans.length - 1]?.location || 'Delivered') : `${carrier} Network`,
      description: isDelivered 
        ? (deliveryInfo.estimatedDelivery || `Package delivered via ${carrier}.`)
        : `Package actively in transit via ${carrier} (${serviceType}).`,
      time: isDelivered ? (carrierScans[carrierScans.length - 1]?.time || 'Delivered') : 'In Transit',
      state: isDelivered ? 'delivered' : 'progress',
      isDeliveryEvent: isDelivered
    }
  ];

  useEffect(() => {
    if (!isOpen || !hasTracking) return;

    setLoadingCarrierScans(true);
    setScansExpanded(true); // Default to showing detail

    fetch(`/api/v1/shipping/track/${encodeURIComponent(carrier)}/${encodeURIComponent(trackingNumber)}`)
      .then(res => res.json())
      .then(data => {
        if (data.success && data.events && data.events.length > 0) {
          const deliveredFlag = data.is_delivered === true || (data.status || '').toLowerCase() === 'delivered';
          setIsDelivered(deliveredFlag);
          setDeliveryInfo({
            status: deliveredFlag ? 'Delivered' : (data.status || 'In Transit'),
            estimatedDelivery: data.estimated_delivery || (deliveredFlag ? 'Delivered' : 'Estimated 2-3 Days')
          });

          // Ensure scans are sorted ASCENDING (oldest pickup first, ending with final delivery)
          const sortedScans = [...data.events].sort((a, b) => {
            const ta = a.raw_time || a.time || '';
            const tb = b.raw_time || b.time || '';
            return ta.localeCompare(tb);
          });
          setCarrierScans(sortedScans);

          if (deliveredFlag && typeof onDelivered === 'function') {
            onDelivered({ carrier, trackingNumber, deliveryData: data });
          }
        } else {
          setDeliveryInfo({
            status: 'In Transit',
            estimatedDelivery: 'In transit to destination'
          });
        }
      })
      .catch(err => {
        console.error('[ShipmentTrackingModal] Error fetching live carrier scans:', err);
      })
      .finally(() => {
        setLoadingCarrierScans(false);
      });
  }, [isOpen, carrier, trackingNumber]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in duration-200">
      <div 
        className="bg-white rounded-2xl max-w-xl w-full p-6 shadow-2xl relative max-h-[90vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex justify-between items-start mb-5 pb-3 border-b shrink-0">
          <div>
            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <Icon name="Truck" size={20} className="text-primary" />
              Shipment Tracking
            </h3>
            <p className="text-xs text-gray-500 font-mono mt-1 flex items-center gap-2">
              <span>{carrier} • {trackingNumber}</span>
              {serviceType && <span className="text-gray-400">({serviceType})</span>}
            </p>
          </div>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-1.5 rounded-lg transition"
            title="Close"
          >
            <Icon name="X" size={18} />
          </button>
        </div>

        {/* Delivery / In Transit Status Banner */}
        <div className="mb-5 shrink-0">
          {isDelivered ? (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 shadow-sm">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Icon name="CheckCircle" size={18} className="text-emerald-600 shrink-0" />
                  <span className="text-[10px] uppercase font-black text-emerald-800 tracking-wider">
                    Package Delivered
                  </span>
                </div>
                {carrierWebUrl && (
                  <a
                    href={carrierWebUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-[11px] font-bold text-emerald-800 hover:text-emerald-950 hover:underline shrink-0"
                    title={`Review live updates on ${carrier} official site`}
                  >
                    <span>Review on {carrier}</span>
                    <Icon name="ExternalLink" size={12} />
                  </a>
                )}
              </div>
              <p className="text-sm font-bold text-emerald-950 mt-1.5 leading-snug">
                {deliveryInfo.estimatedDelivery}
              </p>
            </div>
          ) : (
            <div className="bg-primary/5 border border-primary/10 rounded-xl p-4">
              <div className="flex items-center justify-between gap-2">
                <span className="text-[10px] uppercase font-bold text-gray-400 tracking-wider">
                  Estimated Delivery
                </span>
                {carrierWebUrl && (
                  <a
                    href={carrierWebUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-[11px] font-bold text-primary hover:underline shrink-0"
                    title={`Review live updates on ${carrier} official site`}
                  >
                    <span>Review on {carrier}</span>
                    <Icon name="ExternalLink" size={12} />
                  </a>
                )}
              </div>
              <p className="text-sm font-bold text-primary mt-1">
                {deliveryInfo.estimatedDelivery}
              </p>
            </div>
          )}
        </div>

        {/* Live Loading Indicator */}
        {loadingCarrierScans && (
          <div className="flex items-center justify-center gap-2 p-2.5 mb-4 bg-slate-50 border border-slate-100 text-slate-600 rounded-xl text-xs font-semibold animate-pulse shrink-0">
            <Icon name="Loader" size={14} className="animate-spin text-primary" />
            <span>Syncing live scans with {carrier} API...</span>
          </div>
        )}

        {/* Master Timeline Stages & Detailed Carrier Scans (Ascending) */}
        <div className="space-y-6 relative overflow-y-auto pr-2 flex-1">
          {masterSteps.map((step, index) => {
            const isDeliveredNode = step.isDeliveryEvent || step.status === 'Delivered';
            const isCompleted = step.state === 'completed';
            const isProgress = step.state === 'progress';
            const isCarrierRow = isDeliveredNode || step.id === 'shipped';

            let nodeBg = 'bg-gray-100 text-gray-400 border border-gray-200';
            let circleBg = 'bg-gray-300';
            let titleColor = 'text-gray-400';

            if (isDeliveredNode) {
              nodeBg = 'bg-emerald-600 text-white shadow-md shadow-emerald-500/20';
              circleBg = 'bg-white';
              titleColor = 'text-emerald-700 font-extrabold';
            } else if (isCompleted) {
              nodeBg = 'bg-green-100 text-green-600 border border-green-200';
              circleBg = 'bg-green-500';
              titleColor = 'text-green-700 font-semibold';
            } else if (isProgress) {
              nodeBg = 'bg-blue-600 text-white animate-pulse shadow-md shadow-blue-500/20';
              circleBg = 'bg-white';
              titleColor = 'text-blue-600 font-extrabold';
            }

            return (
              <div key={index} className="flex gap-4 relative w-full">
                {/* Connecting Line */}
                {index < masterSteps.length - 1 && (
                  <div className={`absolute left-[11px] top-5 bottom-[-24px] z-0 ${isCompleted || isDeliveredNode ? 'bg-green-500 w-[4px]' : 'bg-gray-200 w-[2px]'}`} />
                )}

                {/* Node Dot */}
                <div className={`w-6 h-6 rounded-full shrink-0 flex items-center justify-center z-10 ${nodeBg}`}>
                  <div className={`w-2 h-2 rounded-full ${circleBg}`} />
                </div>

                {/* Stage Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center justify-between gap-1.5">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <h4 className={`text-sm font-bold ${titleColor}`}>{step.status}</h4>
                      {isDeliveredNode ? (
                        <span className="bg-emerald-100 text-emerald-800 text-[9px] font-black px-1.5 py-0.5 rounded uppercase tracking-wider flex items-center gap-1">
                          <Icon name="Check" size={10} /> Delivered
                        </span>
                      ) : isCompleted ? (
                        <span className="bg-green-100 text-green-800 text-[9px] font-black px-1.5 py-0.5 rounded uppercase tracking-wider">
                          Completed
                        </span>
                      ) : isProgress ? (
                        <span className="bg-blue-100 text-blue-800 text-[9px] font-black px-1.5 py-0.5 rounded uppercase tracking-wider animate-pulse">
                          In Progress
                        </span>
                      ) : null}
                      <span className="text-[10px] text-gray-400 font-mono">{step.time}</span>
                    </div>

                    {/* Toggle button for carrier detailed scans */}
                    {isCarrierRow && carrierScans.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setScansExpanded(!scansExpanded)}
                        className="text-xs font-bold text-primary bg-primary/10 border border-primary/20 px-2.5 py-1 rounded-lg flex items-center gap-1.5 hover:bg-primary hover:text-white transition shadow-sm"
                      >
                        <Icon name={scansExpanded ? "ChevronUp" : "ChevronDown"} size={13} />
                        {scansExpanded ? 'Hide Details' : 'Show Details'}
                      </button>
                    )}
                  </div>

                  <p className="text-xs text-gray-400 mt-0.5">{step.location}</p>
                  <p className="text-xs text-gray-500 mt-1">{step.description}</p>

                  {/* Carrier Detail Events Timeline (Ascending Sequence Order: Oldest/Pickup -> Final Delivery) */}
                  {isCarrierRow && scansExpanded && carrierScans.length > 0 && (
                    <div className="mt-4 pl-4 border-l-2 border-primary/20 space-y-3 bg-slate-50/80 p-3.5 rounded-xl border border-slate-200/80 animate-in fade-in slide-in-from-top-2 duration-200 text-left">
                      <div className="flex items-center justify-between mb-2 pb-1.5 border-b border-slate-200">
                        <span className="text-[10px] uppercase font-black text-slate-500 tracking-wider flex items-center gap-1.5">
                          <Icon name="Activity" size={12} className="text-primary" />
                          Carrier Detail Sequence (Chronological / Ascending)
                        </span>
                        <span className="text-[10px] text-slate-400 font-mono">
                          {carrierScans.length} events
                        </span>
                      </div>

                      <div className="space-y-3 divide-y divide-slate-100">
                        {carrierScans.map((scan, sIdx) => {
                          const isScanDelivered = scan.is_delivery_event || scan.status === 'Delivered';
                          return (
                            <div key={sIdx} className={`flex gap-3 items-start text-left ${sIdx > 0 ? 'pt-2.5' : ''}`}>
                              <div className={`w-2.5 h-2.5 rounded-full mt-1 shrink-0 ${isScanDelivered ? 'bg-emerald-500 ring-2 ring-emerald-200' : 'bg-primary'}`} />
                              <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-center justify-between gap-1">
                                  <p className={`text-xs font-bold leading-tight ${isScanDelivered ? 'text-emerald-950 font-black' : 'text-slate-800'}`}>
                                    {scan.status}
                                  </p>
                                  <span className="text-[10px] text-slate-500 font-mono">
                                    {scan.time || 'Scan'}
                                  </span>
                                </div>
                                <p className="text-[11px] text-slate-500 mt-0.5 font-medium">
                                  {scan.location || 'Courier Network'}
                                </p>
                                {scan.description && scan.description !== scan.status && (
                                  <p className="text-[11px] text-slate-600 mt-0.5 italic">
                                    {scan.description}
                                  </p>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer Actions */}
        <div className="mt-5 pt-3 border-t flex items-center justify-between gap-3 shrink-0">
          <div>
            {carrierWebUrl && (
              <a
                href={carrierWebUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-xs font-semibold text-slate-700 hover:text-primary transition shadow-sm"
              >
                <Icon name="ExternalLink" size={13} className="text-primary" />
                Review on {carrier} Site
              </a>
            )}
          </div>
          <Button variant="default" onClick={onClose}>
            Close Window
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ShipmentTrackingModal;
